import React, { useState, useEffect } from 'react';
import cardsData from '../data/cards.json';
import './CardGame.css';

// Card types with their titles and subtitles
const CARD_TYPES = {
  freaky: {
    title: "Freaky Challenge",
    type: "Drink or Do"
  },
  never: {
    title: "Never Have I Ever",
    type: "Caught Out? Drink!"
  },
  countdown: {
    title: "321 Challenge",
    type: "Count down from 3 and say your answers at the same time"
  },
  miniGame: {
    title: "Mini Game",
    type: "Play together - loser drinks!"
  },
  drinkIf: {
    title: "Drink If...",
    type: "Truth or Booze!"
  }
};

// Define the card type
interface Card {
  id: number;
  cardType: string;
  content: string;
}

// Create a combined array of all card types
const getAllCards = (): Card[] => {
  const cards: Card[] = [];
  
  // Add freaky cards
  cardsData.freakyCards.forEach((content, index) => {
    cards.push({
      id: cards.length + 1,
      cardType: 'freaky',
      content
    });
  });
  
  // Add never have I ever cards
  cardsData.neverHaveIEver.forEach((content, index) => {
    cards.push({
      id: cards.length + 1,
      cardType: 'never',
      content
    });
  });
  
  // Add countdown challenge cards
  cardsData.countdownChallenges.forEach((content, index) => {
    cards.push({
      id: cards.length + 1,
      cardType: 'countdown',
      content
    });
  });
  
  // Add mini game cards
  cardsData.miniGames.forEach((content, index) => {
    cards.push({
      id: cards.length + 1,
      cardType: 'miniGame',
      content
    });
  });
  
  // Add drink if cards
  cardsData.drinkIf.forEach((content, index) => {
    cards.push({
      id: cards.length + 1,
      cardType: 'drinkIf',
      content
    });
  });
  
  return cards;
};

// Shuffle the cards using Fisher-Yates algorithm
const shuffleCards = (cards: Card[]): Card[] => {
  const shuffled = [...cards];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const CardGame: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [isTapped, setIsTapped] = useState(false);

  // Initialize the game with shuffled cards
  useEffect(() => {
    setCards(shuffleCards(getAllCards()));
  }, []);

  // Add this useEffect to handle scroll locking
  useEffect(() => {
    // Function to sync height and manage scrolling
    const syncHeight = () => {
      const height = window.innerHeight;
      document.documentElement.style.setProperty(
        '--window-inner-height',
        `${height}px`
      );
    };

    // Initial sync
    syncHeight();

    // Listen for resize events
    window.addEventListener('resize', syncHeight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', syncHeight);
    };
  }, []);

  // Handle navigation to previous card
  const prevCard = () => {
    // Don't reset flip state immediately
    setIsTapped(false);
    setSlideDirection('slide-right');
    
    setTimeout(() => {
      // Reset flip state here, just before changing the card
      setIsFlipped(false);
      setCurrentCardIndex((prevIndex) => 
        prevIndex === 0 ? cards.length - 1 : prevIndex - 1
      );
      setSlideDirection('slide-from-left');
      
      setTimeout(() => {
        setSlideDirection('');
      }, 300);
    }, 200);
  };

  // Handle navigation to next card
  const nextCard = () => {
    // Don't reset flip state immediately
    setIsTapped(false);
    setSlideDirection('slide-left');
    
    setTimeout(() => {
      // Reset flip state here, just before changing the card
      setIsFlipped(false);
      setCurrentCardIndex((prevIndex) => 
        prevIndex === cards.length - 1 ? 0 : prevIndex + 1
      );
      setSlideDirection('slide-from-right');
      
      setTimeout(() => {
        setSlideDirection('');
      }, 300);
    }, 200);
  };

  // Handle card flipping
  const flipCard = () => {
    const nextFlippedState = !isFlipped;
    setIsFlipped(nextFlippedState);
    // Only set tapped to true when flipping to the back
    // Reset tapped when flipping back to the front
    setIsTapped(nextFlippedState);
  };

  if (cards.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  const currentCard = cards[currentCardIndex];
  const cardTypeInfo = CARD_TYPES[currentCard.cardType as keyof typeof CARD_TYPES];
  
  // Set card class based on type
  const getCardClassName = (cardType: string) => {
    if (cardType === 'freaky') return "card-freaky";
    if (cardType === 'never') return "card-never";
    if (cardType === 'countdown') return "card-countdown";
    if (cardType === 'miniGame') return "card-mini-game";
    if (cardType === 'drinkIf') return "card-drink-if";
    return "";
  };
  

  return (
    <div className="card-game">
      <h1 className="game-title">Couples Drinking Game</h1>
      
      <div className="card-container">
        <div 
          className={`card ${getCardClassName(currentCard.cardType)} ${isFlipped ? 'flipped' : ''} ${slideDirection}`}
          onClick={flipCard}
        >
          <div className="card-inner">
            <div className="card-front">
              <h2>{cardTypeInfo.title}</h2>
              <p className="card-type">{cardTypeInfo.type}</p>
              {!isTapped && <div className="tap-indicator">Tap to reveal</div>}
            </div>
            <div className="card-back">
              <h2>{cardTypeInfo.title}</h2>
              <p className="card-type">{cardTypeInfo.type}</p>
              <p className="card-content">{currentCard.content}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card-navigation">
        <button className="nav-button prev" onClick={prevCard}>
          &larr;
        </button>
        <button className="nav-button next" onClick={nextCard}>
          &rarr;
        </button>
      </div>

      <div className="card-counter">
        {currentCardIndex + 1} / {cards.length}
      </div>
    </div>
  );
};

export default CardGame; 